import React, { useState, useRef, useEffect } from 'react'
import { getImageUrl } from '../utils/imageUtils'

const OptimizedImage = ({
  src,
  alt,
  className = '',
  width,
  height,
  quality = 85,
  loading = 'lazy',
  placeholder = 'blur',
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const [imageSrc, setImageSrc] = useState('')
  const imgRef = useRef(null)

  // Generate WebP and fallback sources with query parameters for optimization
  const generateSources = (originalSrc) => {
    if (!originalSrc) return { webp: '', fallback: originalSrc, avif: '' }
    
    // Check if it's a Cloudinary URL
    const isCloudinary = originalSrc.includes('cloudinary.com');

    if (isCloudinary) {
      return {
        webp: getImageUrl(originalSrc, { quality, format: 'webp' }),
        avif: getImageUrl(originalSrc, { quality, format: 'avif' }),
        fallback: getImageUrl(originalSrc, { quality, format: 'auto' })
      };
    }

    // For local/other images, don't fake formats
    return {
      webp: '',
      avif: '',
      fallback: originalSrc
    }
  }

  const sources = generateSources(src)

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (loading === 'eager') {
      setIsInView(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    )

    if (imgRef.current) {
      observer.observe(imgRef.current)
    }

    return () => observer.disconnect()
  }, [loading])

  // Set image source when in view
  useEffect(() => {
    if (isInView && src) {
      setImageSrc(src)
    }
  }, [isInView, src])

  // Handle image load
  const handleLoad = () => {
    setIsLoaded(true)
  }

  // Handle image error - fallback to original
  const handleError = () => {
    if (imageSrc !== sources.fallback) {
      setImageSrc(sources.fallback)
    }
  }

  // Blur placeholder SVG
  const blurPlaceholder = `data:image/svg+xml;base64,${btoa(`
    <svg width="${width || 400}" height="${height || 300}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#f3f4f6;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#e5e7eb;stop-opacity:1" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#grad)"/>
    </svg>
  `)}`

  return (
    <div 
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={{ width, height }}
    >
      {/* Placeholder */}
      {placeholder === 'blur' && !isLoaded && (
        <div 
          className="absolute inset-0 bg-gray-200 animate-pulse"
          style={{
            backgroundImage: `url(${blurPlaceholder})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
      )}

      {/* Loading skeleton */}
      {!isLoaded && (
        <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-pulse">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-50 animate-shimmer"></div>
        </div>
      )}

      {/* Optimized Image */}
      {isInView && (
        <picture>
          {/* AVIF format for modern browsers */}
          {sources.avif && (
            <source 
              srcSet={sources.avif} 
              type="image/avif"
            />
          )}
          
          {/* WebP format */}
          {sources.webp && (
            <source 
              srcSet={sources.webp} 
              type="image/webp"
            />
          )}
          
          {/* Fallback */}
          <img
            src={imageSrc}
            alt={alt}
            loading={loading}
            onLoad={handleLoad}
            onError={handleError}
            className={`transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            } w-full h-full object-cover`}
            {...props}
          />
        </picture>
      )}

      {/* Loading indicator */}
      {!isLoaded && isInView && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary-dark border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  )
}

export default OptimizedImage
